import { useEffect, useState } from 'react';
import { garageAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { PageHeader, Button, Table, StatusBadge, Modal, Input, Select, Loading, Alert } from '../components/ui';

const empty = {
  name:'', location:'', phone:'', email:'', type:'external', status:'active', notes:'', specialties:'[]',
  coord_person:'', coord_designation:'', agent_code:'',
  whatsapp_no:'', mobile_no:'', telephone_no:'',
  tin_no:'', vat_no:'',
  account_name:'', account_no:'', bank:'', bank_code:'', bank_branch:'', branch_code:'',
};

// Category types the supplier deals in
export const SPECIALTIES = [
  { key:'insurance',  label:'🛡 Insurance' },
  { key:'ac',         label:'❄️ AC' },
  { key:'mechanical', label:'🔧 Mechanical' },
  { key:'tyre',       label:'⭕ Tyre' },
  { key:'service',    label:'⚙️ Service' },
  { key:'battery',    label:'🔋 Battery' },
  { key:'electrical', label:'⚡ Electrical' },
  { key:'hydraulic',  label:'💧 Hydraulic' },
  { key:'hybrid',     label:'🔌 Hybrid' },
  { key:'paint',      label:'🎨 Paint & Tinkering' },
  { key:'cushion',    label:'🛋 Cushion' },
  { key:'wash',       label:'🚿 Wash & Vacuum' },
  { key:'vehiclesale',label:'🚗 Vehicle Sale' },
  { key:'spareparts', label:'🔩 Spare Parts' },
];
const parseSpecs = (v) => { try { const a = JSON.parse(v || '[]'); return Array.isArray(a) ? a : []; } catch { return []; } };

export default function Garages() {
  const { can } = useAuth();
  const [garages, setGarages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState('');
  const [success, setSuccess] = useState('');
  const [modal,   setModal]   = useState(false);
  const [editing, setEditing] = useState(null);
  const [form,    setForm]    = useState(empty);
  const [saving,  setSaving]  = useState(false);
  const [filterType, setFilterType] = useState('');

  const flash = (msg, isErr=false) => {
    isErr ? setError(msg) : setSuccess(msg);
    setTimeout(() => isErr ? setError('') : setSuccess(''), 3500);
  };

  const load = () => {
    setLoading(true);
    garageAPI.getAll()
      .then(r => setGarages(r.data.data))
      .catch(() => flash('Failed to load garages', true))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const openAdd  = () => { setEditing(null); setForm(empty); setModal(true); };
  const openEdit = g  => { setEditing(g); setForm({...empty, ...g, specialties: g.specialties || '[]'}); setModal(true); };

  const save = async e => {
    e.preventDefault(); setSaving(true);
    try {
      editing ? await garageAPI.update(editing.id, form) : await garageAPI.create(form);
      setModal(false); load();
      flash(editing ? 'Supplier updated' : 'Supplier added');
    } catch (err) { flash(err.response?.data?.message || 'Save failed', true); }
    finally { setSaving(false); }
  };

  const filtered = filterType ? garages.filter(g => g.type === filterType) : garages;
  const internal = garages.filter(g => g.type === 'internal').length;
  const external = garages.filter(g => g.type === 'external').length;

  return (
    <div>
      <PageHeader
        title="Suppliers & Garages"
        sub={`${garages.length} total · ${internal} internal · ${external} external`}
        action={can('admin') && <Button onClick={openAdd}>+ Add Supplier</Button>}
      />

      {error   && <div className="mb-4"><Alert type="error"   message={error}   /></div>}
      {success && <div className="mb-4"><Alert type="success" message={success} /></div>}

      {/* Filter pills */}
      <div className="flex gap-2 mb-5">
        {[
          { val:'', label:`All (${garages.length})` },
          { val:'internal', label:`Internal (${internal})` },
          { val:'external', label:`External (${external})` },
        ].map(f => (
          <button key={f.val} onClick={() => setFilterType(f.val)}
            className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all
              ${filterType === f.val ? 'bg-primary-500 text-white border-primary-500' : 'bg-white text-gray-600 border-gray-300 hover:border-primary-400'}`}>
            {f.label}
          </button>
        ))}
      </div>

      {loading ? <Loading /> : (
        <Table headers={['Supplier','Type','Address','Contact','Bank / TIN','Status','Actions']}>
          {filtered.map(g => (
            <tr key={g.id} className="hover:bg-gray-50">
              <td className="px-4 py-3">
                <p className="font-medium text-sm text-gray-800">{g.name}</p>
                {g.notes && <p className="text-xs text-gray-400 truncate max-w-[180px]">{g.notes}</p>}
                {parseSpecs(g.specialties).length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-1">
                    {parseSpecs(g.specialties).map(k => {
                      const sp = SPECIALTIES.find(x => x.key === k);
                      return sp ? <span key={k} className="px-1.5 py-0.5 rounded-full bg-primary-50 text-primary-700 text-[10px]">{sp.label}</span> : null;
                    })}
                  </div>
                )}
              </td>
              <td className="px-4 py-3">
                <span className={`px-2 py-0.5 rounded-full text-xs font-medium capitalize
                  ${g.type === 'internal' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'}`}>
                  {g.type}
                </span>
              </td>
              <td className="px-4 py-3 text-sm text-gray-600">{g.location || '—'}</td>
              <td className="px-4 py-3 text-sm text-gray-600">
                {g.coord_person && <p className="font-medium text-gray-700">{g.coord_person}
                  {g.coord_designation ? <span className="text-xs text-gray-400"> · {g.coord_designation}</span> : null}</p>}
                {(g.mobile_no || g.phone) && <p className="text-xs">{g.mobile_no || g.phone}</p>}
                {g.whatsapp_no && <p className="text-xs text-green-600">WA {g.whatsapp_no}</p>}
                {g.email && <p className="text-xs text-gray-400">{g.email}</p>}
              </td>
              <td className="px-4 py-3 text-sm text-gray-600">
                {g.bank && <p className="text-xs">{g.bank}{g.bank_branch ? ` — ${g.bank_branch}` : ''}</p>}
                {g.account_no && <p className="text-xs font-mono text-gray-500">{g.account_no}</p>}
                {g.vat_no && <p className="text-xs text-gray-400">VAT {g.vat_no}</p>}
                {!g.bank && !g.account_no && !g.vat_no && <span className="text-gray-300">—</span>}
              </td>
              <td className="px-4 py-3"><StatusBadge status={g.status} /></td>
              <td className="px-4 py-3">
                {can('admin') && (
                  <Button variant="ghost" size="sm" onClick={() => openEdit(g)}>Edit</Button>
                )}
              </td>
            </tr>
          ))}
          {filtered.length === 0 && (
            <tr><td colSpan={7} className="px-4 py-10 text-center text-gray-400">No garages found</td></tr>
          )}
        </Table>
      )}

      <Modal open={modal} onClose={() => setModal(false)}
        title={editing ? `Edit Supplier — ${editing.name}` : 'Add Supplier / Garage'} size="xl">
        <form onSubmit={save} className="space-y-4">
          <Input label="Name *" value={form.name}
            onChange={e => setForm({...form, name: e.target.value})} required
            placeholder="e.g. AutoCare (Pvt) Ltd" />
          <div className="grid grid-cols-2 gap-4">
            <Select label="Type" value={form.type}
              onChange={e => setForm({...form, type: e.target.value})}>
              <option value="external">External</option>
              <option value="internal">Internal (In-house)</option>
            </Select>
            <Select label="Status" value={form.status}
              onChange={e => setForm({...form, status: e.target.value})}>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </Select>
          </div>
          <Input label="Supplier Address" value={form.location}
            onChange={e => setForm({...form, location: e.target.value})}
            placeholder="e.g. No 45, Galle Road, Colombo 03" />

          {/* Coordinating person */}
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
            <p className="text-xs font-semibold text-blue-700 uppercase tracking-wide mb-3">👤 Coordinating Person</p>
            <div className="grid grid-cols-3 gap-4">
              <Input label="Name" value={form.coord_person}
                onChange={e => setForm({...form, coord_person: e.target.value})} placeholder="e.g. K. Silva" />
              <Input label="Designation" value={form.coord_designation}
                onChange={e => setForm({...form, coord_designation: e.target.value})} placeholder="e.g. Service Manager" />
              <Input label="Agent Code" value={form.agent_code}
                onChange={e => setForm({...form, agent_code: e.target.value})} />
            </div>
          </div>

          {/* Contact numbers */}
          <div className="bg-gray-50 border border-gray-200 rounded-xl p-4">
            <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-3">📞 Contact Details</p>
            <div className="grid grid-cols-3 gap-4">
              <Input label="WhatsApp" value={form.whatsapp_no}
                onChange={e => setForm({...form, whatsapp_no: e.target.value})} placeholder="07X XXXXXXX" />
              <Input label="Mobile" value={form.mobile_no}
                onChange={e => setForm({...form, mobile_no: e.target.value})} placeholder="07X XXXXXXX" />
              <Input label="Telephone" value={form.telephone_no}
                onChange={e => setForm({...form, telephone_no: e.target.value})} placeholder="0112234567" />
              <Input label="General Phone" value={form.phone}
                onChange={e => setForm({...form, phone: e.target.value})} placeholder="0112234567" />
              <div className="col-span-2">
                <Input label="Email" type="email" value={form.email}
                  onChange={e => setForm({...form, email: e.target.value})} placeholder="info@supplier.lk" />
              </div>
            </div>
          </div>

          {/* Tax registration */}
          <div className="grid grid-cols-2 gap-4">
            <Input label="TIN No." value={form.tin_no}
              onChange={e => setForm({...form, tin_no: e.target.value})} />
            <Input label="VAT No." value={form.vat_no}
              onChange={e => setForm({...form, vat_no: e.target.value})} />
          </div>

          {/* Bank account */}
          <div className="bg-green-50 border border-green-200 rounded-xl p-4">
            <p className="text-xs font-semibold text-green-700 uppercase tracking-wide mb-3">🏦 Account Details (for payments)</p>
            <div className="grid grid-cols-3 gap-4">
              <Input label="Account Name" value={form.account_name}
                onChange={e => setForm({...form, account_name: e.target.value})} />
              <Input label="Account No." value={form.account_no}
                onChange={e => setForm({...form, account_no: e.target.value})} />
              <Input label="Bank" value={form.bank}
                onChange={e => setForm({...form, bank: e.target.value})} placeholder="e.g. Bank of Ceylon" />
              <Input label="Bank Code" value={form.bank_code}
                onChange={e => setForm({...form, bank_code: e.target.value})} />
              <Input label="Branch" value={form.bank_branch}
                onChange={e => setForm({...form, bank_branch: e.target.value})} />
              <Input label="Branch Code" value={form.branch_code}
                onChange={e => setForm({...form, branch_code: e.target.value})} />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Category Type <span className="text-xs font-normal text-gray-400">— what this supplier provides</span></label>
            <div className="grid grid-cols-3 gap-2 mb-4">
              {SPECIALTIES.map(sp => {
                const selected = parseSpecs(form.specialties);
                const checked = selected.includes(sp.key);
                return (
                  <label key={sp.key}
                    className={`flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer text-sm
                      ${checked ? 'border-primary-500 bg-primary-50 text-primary-700' : 'border-gray-200 hover:border-gray-300'}`}>
                    <input type="checkbox" checked={checked}
                      onChange={() => {
                        const next = checked ? selected.filter(x => x !== sp.key) : [...selected, sp.key];
                        setForm({ ...form, specialties: JSON.stringify(next) });
                      }} />
                    {sp.label}
                  </label>
                );
              })}
            </div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
            <textarea value={form.notes} onChange={e => setForm({...form, notes: e.target.value})}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg outline-none
                         focus:ring-2 focus:ring-primary-300 h-16 resize-none"
              placeholder="Speciality, notes, etc." />
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <Button variant="ghost" onClick={() => setModal(false)}>Cancel</Button>
            <Button type="submit" disabled={saving}>{saving ? 'Saving…' : 'Save'}</Button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
